/// <amd-module name="Widgets/VelocityWidget/VelocityWidgetComponents/VelocityDataHelper" />
import { Iteration } from "Analytics/Boards/BoardsQueries/IterationsQuery";
import { WitFieldUtilities } from "Analytics/Boards/BoardsQueries/WitFieldUtilities";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { AggregationMode } from "WidgetCommon/ModernWidgetTypes/CommonConfigurationTypes";
import { ModefulValueSetting } from "WidgetCommon/ModernWidgetTypes/ModefulValueSetting";
import { WorkItemStateCategory } from "WidgetCommon/WitUtils/Constants";
import * as WidgetResources from "Widgets/VelocityWidget/Resources";
import { VelocityConstants } from "./VelocityConstants";
import { Metastate, WorkItemTypeField } from "./VelocityDataContract";

/**
 *  Helper methods for data handling in the Velocity Widget
 */
export class VelocityDataHelper {
    public static getScalarMeasure(
        aggregationWorkItemTypeFields: WorkItemTypeField[],
        aggregation: ModefulValueSetting<AggregationMode, string>
    ): string {
        switch (aggregation.identifier) {
            case AggregationMode.Sum:
                return VelocityDataHelper.getWorkItemTypeFieldName(aggregationWorkItemTypeFields, aggregation.settings);
            case AggregationMode.Count:
                return WidgetResources.VelocityWidget_CountOfWorkItems;
        }
    }

    public static getQueryAggregation(aggregation: ModefulValueSetting<AggregationMode, string>): string {
        switch (aggregation.identifier) {
            case AggregationMode.Sum:
                const odataPropertyName = WitFieldUtilities.getFieldODataPropertyName(aggregation.settings);
                if (odataPropertyName == null || odataPropertyName.trim().length === 0) {
                    throw new Error(format(WidgetResources.VelocityWidget_NoPropNameFoundForAggregation, aggregation.settings));
                }

                return `aggregate(${odataPropertyName} with sum as ${VelocityConstants.queryResult})`;
            case AggregationMode.Count:
                return `aggregate($count as ${VelocityConstants.queryResult})`;
            default:
                throw new Error(format(WidgetResources.VelocityWidget_UnexpectedAggregationError, aggregation.identifier));
        }
    }

    public static isWorkItemCompleted(StateCategory: string, State: string, showResolvedItemsAsCompleted = false) : boolean {
        if(StateCategory ===  WorkItemStateCategory[WorkItemStateCategory.Completed]){
            return true;
        }

        if(showResolvedItemsAsCompleted){
            return (
                StateCategory ===  WorkItemStateCategory[WorkItemStateCategory.Resolved] ||
                State === WorkItemStateCategory[WorkItemStateCategory.Resolved]
            );
        }

        return false;
    }

    public static isWorkItemInProgress(StateCategory: string, State: string, showResolvedItemsAsCompleted = false) : boolean {
        if(showResolvedItemsAsCompleted){
            return (
                StateCategory ===  WorkItemStateCategory[WorkItemStateCategory.InProgress] &&
                State !== WorkItemStateCategory[WorkItemStateCategory.Resolved]
            );
        }
        else{
            return StateCategory ===  WorkItemStateCategory[WorkItemStateCategory.InProgress];
        }
    }

    public static getAverageVelocity(metastates: Metastate[], iterations: Iteration[], showResolvedItemsAsCompleted = false): number {
        let velocitySum = 0;
        let completedIterationCount = 0;

        iterations.map((iteration: Iteration) => {
            if (iteration.IsEnded) {
                metastates.map((metastate: Metastate) => {
                    if (
                        VelocityDataHelper.isWorkItemCompleted(metastate.StateCategory, metastate.State, showResolvedItemsAsCompleted) &&
                        metastate.IterationSK === iteration.IterationSK
                    ) {
                        velocitySum += metastate.AggregationResult;
                    }
                });

                completedIterationCount++;
            }
        });

        return completedIterationCount > 0 ? Math.round(velocitySum / completedIterationCount) : 0;
    }

    public static getSubtitle(iterationCount: number) {
        if (iterationCount === 1) {
            return WidgetResources.VelocityWidget_SingleIterationSubtitle;
        } else if (iterationCount > 1) {
            return format(WidgetResources.VelocityWidget_IterationsSubtitle, iterationCount.toString());
        } else {
            return WidgetResources.VelocityWidget_NoIterations;
        }
    }

    /**
     * Looks up the work item type field name from a list of queried work item type fields.
     * Method is public for unit testing.
     * @param aggregationWorkItemTypeFields
     * @param aggregationSetting
     */
    public static getWorkItemTypeFieldName(aggregationWorkItemTypeFields: WorkItemTypeField[], aggregationSetting: string): string {
        let fieldName = "";

        for (const workItemTypeField of aggregationWorkItemTypeFields) {
            if (!localeIgnoreCaseComparer(workItemTypeField.FieldReferenceName, aggregationSetting)) {
                fieldName = workItemTypeField.FieldName;
                break;
            }
        }

        return fieldName;
    }
}
